import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { toast } from "../components/index.js";

/**
 * log-view shows plain text as numbered lines, with a line filter, a wrap
 * switch and a copy button. The filter keeps each line's own number. The
 * element fills the height its parent gives it and scrolls inside.
 *
 *   <log-view .text=${log} style="max-height:30rem"></log-view>
 */
@customElement("log-view")
export class LogView extends LitElement {
  @property() text = "";
  /** Starts without line wrap, for text with very long lines. */
  @property({ type: Boolean }) nowrap = false;

  @state() private filter = "";
  /** The user's wrap choice; unset until they change it. */
  @state() private wrapChoice?: boolean;

  private lines: string[] = [];

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 0;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .bar {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      padding-bottom: var(--bino-space-sm);
      flex-shrink: 0;
    }
    bn-input {
      flex: 0 1 18rem;
    }
    .count {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .spacer {
      flex: 1;
    }
    .scroll {
      flex: 1;
      min-height: 0;
      overflow: auto;
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius);
    }
    .lines {
      padding: var(--bino-space-sm) 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      line-height: 1.5;
      width: max-content;
      min-width: 100%;
      box-sizing: border-box;
    }
    .lines.wrap {
      width: auto;
    }
    .line {
      position: relative;
      min-height: 1.5em;
      white-space: pre;
      padding: 0 var(--bino-space-md) 0 calc(var(--digits) * 1ch + 3ch);
    }
    .wrap .line {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .line::before {
      content: attr(data-n);
      position: absolute;
      left: 0;
      width: calc(var(--digits) * 1ch + 1ch);
      text-align: right;
      color: var(--bino-text-secondary);
    }
    .none {
      margin: 0;
      padding: var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
  `;

  override willUpdate(changed: PropertyValues) {
    if (changed.has("text")) {
      this.lines = this.text.split(/\r?\n/);
      if (this.lines[this.lines.length - 1] === "") this.lines.pop();
    }
  }

  private async copy() {
    try {
      await navigator.clipboard.writeText(this.text);
      toast("Copied to clipboard", "success");
    } catch {
      toast("Couldn't copy. Select the text and copy it yourself.", "error");
    }
  }

  override render() {
    const wrap = this.wrapChoice ?? !this.nowrap;
    const needle = this.filter.toLowerCase();
    const shown = this.lines
      .map((line, i) => ({ line, n: i + 1 }))
      .filter((l) => !needle || l.line.toLowerCase().includes(needle));
    return html`
      <div class="bar">
        <bn-input
          placeholder="Filter lines"
          .value=${this.filter}
          @input=${(e: CustomEvent) => (this.filter = e.detail.value)}
        ></bn-input>
        ${needle
          ? html`<span class="count">${shown.length} of ${this.lines.length} lines</span>`
          : nothing}
        <span class="spacer"></span>
        <bn-checkbox
          label="Wrap lines"
          .checked=${wrap}
          @change=${(e: CustomEvent) => (this.wrapChoice = e.detail.checked)}
        ></bn-checkbox>
        <bn-button variant="ghost" size="sm" @click=${() => this.copy()}>Copy</bn-button>
      </div>
      <div class="scroll" tabindex="0">
        ${shown.length === 0
          ? html`<p class="none">${needle ? "No line matches." : "Empty."}</p>`
          : html`<div
              class="lines ${wrap ? "wrap" : ""}"
              style="--digits:${String(this.lines.length).length}"
            >
              ${shown.map((l) => html`<div class="line" data-n=${l.n}>${l.line}</div>`)}
            </div>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "log-view": LogView;
  }
}
